import FullScreenModal from 'live/components/ui/FullScreenModal'
import GlobalAlert from 'live/components/ui/global-alert'
import Tooltips from 'live/components/ui/tooltip'
import globalConfirmation from 'live/services/global-confirmation'
import i18n from 'live/services/i18n'
import _ from 'lodash' // @ts-ignore: Unreachable code error
import { ModalContent } from 'plugin-wells/shared/styles/CommonStyle'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import ConfigApi, { convertConstants } from '../api/ConfigApi'
import ConfigAlgorithm from '../constants/ConfigAlgorithm'
import { Button, OutlineButton } from '../styles/Commonstyles'
import ConstantConfigType, { UnitConfig } from '../types/ConstantConfigType'
import { PresetDataType, PresetType } from './ModelsConfigTypes'
import { getPhysicalModelParameters } from './ModelsSettings'
import PresetList from './components/PresetList'
import { ActivatePresetModal, DeletePresetModal, NewPresetModal } from './components/PresetModal'
import PresetSheet from './components/PresetSheet'
import {
    STANDARD_PARAMETERS,
    deletePreset,
    dismissAll,
    getGlobalPreset,
    getListPreset,
    getPresetDataById,
    savePreset,
    updatePreset
} from './components/pressets/api'

const LOCAL_UNIT_SET_NAME = 'live-rig-state/config/unitset/name'
const LOCAL_UNIT_SET_TIMESTAMP = 'live-rig-state/config/unitset/timestamp'

export default function ModelsConfigForm({
    show,
    setShow,
    assetName,
    assetId
}: {
    show: boolean
    setShow: Function
    assetName: string
    assetId: string
}): React.ReactElement {
    const [changed, setChanged] = useState(false)
    const [selectedPreset, setSelectedPreset] = useState<PresetType>({} as PresetType)
    const [presetList, setPresetList] = useState<PresetType[]>([])
    const [presetPhysicalParameters, setPresetPhysicalParameters] = useState<PresetDataType>({})

    const [showNewPresetModal, setShowNewPresetModal] = useState(false)
    const [showDuplicatePresetModal, setShowDuplicatePresetModal] = useState(false)
    const [showChangePresetModal, setShowChangePresetModal] = useState(false)
    const [showDeletePresetModal, setShowDeletePresetModal] = useState(false)
    const [showActivatePresetModal, setShowActivatePresetModal] = useState(false)
    const [globalChanged, setGlobalChanged] = useState(false)
    const [globalPreset, setGlobalPreset] = useState({} as PresetDataType)
    const [unitSetName, setUnitSetName] = useState<UnitConfig>('meter')

    const [convertedGlobalPreset, setConvertedGlobalPreset] = useState({} as PresetDataType)
    const [convertedPhysicalModelParameters, setConvertedPhysicalModelParameters] = useState({} as PresetDataType)
    const [convertedSelectedPreset, setConvertedSelectedPreset] = useState({} as PresetType)

    const [editTable, setEditTable] = useState(false)

    const [alertMessage, setAlertMessage] = useState('')
    const [alertTitle, setAlertTitle] = useState('')
    const [alertType, setAlertType] = useState<'success' | 'danger' | 'warning' | 'info'>('success')
    const [showalertMessage, setShowAlertMessage] = useState(false)
    const [neverClose, setNeverClose] = useState(false)

    const getListPresetRequest = useCallback(
        (assetId: string, callback) => {
            getListPreset(assetId, callback)
        },
        [assetId, presetList]
    )

    const getPresetDataByIdRequest = useCallback(
        (rigId: string, id, callback) => {
            getPresetDataById(rigId, id, callback)
        },
        [selectedPreset]
    )

    const changeSelectedPreset = (preset: PresetType) => {
        convertPreset(
            preset.parameters,
            newParameters => {
                const newPreset = _.cloneDeep(preset)
                newPreset.parameters = _.cloneDeep(newParameters)
                handleUnSave(
                    () => {
                        setEditTable(false)
                        setSelectedPreset(newPreset)
                        setPresetPhysicalParameters(newParameters || ({} as PresetDataType))
                        setChanged(false)
                    },
                    () => {
                        setSelectedPreset(newPreset)
                    }
                )
            },
            unitSetName,
            'default'
        )
    }

    const handleUnSave = (onConfirm: Function, onCancel?: Function) => {
        if (changed) {
            globalConfirmation.danger(
                <span>{i18n('There are unsaved changes, are you sure you want to leave ?')}</span>,
                null,
                () => {
                    setChanged(false)
                    handleCancel()
                    setEditTable(false)

                    onCancel?.()
                },
                { btnText: i18n('Yes') }
            )
        } else {
            setChanged(false)
            onConfirm()
        }
    }

    const handleClose = () => {
        setPresetList([])
        setPresetPhysicalParameters({ message: 'handleClose' })
        setSelectedPreset({} as PresetType)
        setShow(false)
        setEditTable(false)
        setChanged(false)
    }

    const validatePreset = (preset: PresetType): string | null => {
        if (!preset.parameters) return i18n('Empty parameters object.')
        const { blockWeightToleranceThreshold, hookLoadRatioThreshold } = preset.parameters

        if (blockWeightToleranceThreshold.value > hookLoadRatioThreshold.value) {
            return i18n("The Block Weight Tolerance Threshold shouldn't be greater than Hook Load Ratio Threshold.")
        }

        return null
    }

    const handleSubmit = useCallback(
        (local, global) => {
            for (const [key, { value }] of Object.entries(local)) {
                if (value === undefined || value === null || value === '') {
                    showAlert('', i18n('Missing') + ` ${key} ` + i18n('value'), 'danger')

                    return
                }
            }
            const presetInfos: PresetType = {
                ...selectedPreset,
                parameters: parseNumberPreset(local, global)
            }
            const message = validatePreset(presetInfos)

            if (message) {
                showAlert('', message, 'danger')

                return
            }
            convertPreset(
                presetInfos.parameters,
                newParameters => {
                    const newPreset = _.cloneDeep(selectedPreset)
                    newPreset.parameters = newParameters

                    updatePreset(assetId, newPreset, response => {
                        if (response.success) {
                            setSelectedPreset(response.preset)
                            setPresetPhysicalParameters(response.preset.parameters)

                            setChanged(false)
                            setEditTable(false)

                            showAlert(i18n('Success!'), i18n('_preset_save_message_', { name: selectedPreset.name }))
                        } else {
                            showAlert(i18n('Error!'), i18n('Not saved'), 'danger')
                        }
                    })
                },
                unitSetName,
                'default'
            )
        },
        [selectedPreset?.id, unitSetName]
    )

    const handleDelete = () => {
        deletePreset(assetId, selectedPreset?.id, resp => {
            if (resp.success) {
                setPresetList(prevList => {
                    return prevList.filter(item => item.id !== resp.presetId)
                })
            }
        })

        setShowDeletePresetModal(false)

        showAlert(i18n('Success!'), i18n('_preset_delete_message_', { name: selectedPreset.name }))
    }

    const handleDuplicate = (name: string) => {
        convertPreset(
            parseNumberPreset(convertedPhysicalModelParameters, globalPreset),
            respPreset => {
                const newPreset = {
                    active: true,
                    name,
                    id: `unsaved-preset-${name}`,
                    parameters: respPreset,
                    timestamp: new Date().getTime()
                }
                const message = validatePreset(newPreset)
                if (message) {
                    showAlert(message, '', 'danger')

                    return
                }
                savePreset(assetId, newPreset as PresetType, response => {
                    if (response.success) {
                        setPresetList(prev => [...prev, response.preset])

                        setSelectedPreset(response.preset)
                        setPresetPhysicalParameters(response?.preset?.parameters || globalPreset)

                        showAlert(i18n('Success!'), i18n('_preset_create_message_', { name }))

                        setShowDuplicatePresetModal(false)
                    }
                })
            },
            unitSetName,
            'default'
        )
    }

    const handleNewPreset = name => {
        convertPreset(
            parseNumberPreset(convertedGlobalPreset, globalPreset),
            respPreset => {
                const newPreset = {
                    active: false,
                    name,
                    id: `unsaved-preset-${name}`,
                    parameters: respPreset,
                    timestamp: new Date().getTime()
                }

                savePreset(assetId, newPreset as PresetType, response => {
                    if (response.success) {
                        setPresetList(prev => [...prev, response.preset])

                        setSelectedPreset({ ...response.preset })
                        setPresetPhysicalParameters(response?.preset?.parameters || globalPreset)
                    }
                })

                showAlert(i18n('Success!'), i18n('_preset_create_message_', { name }))

                setShowNewPresetModal(false)
            },
            unitSetName,
            'default'
        )
    }

    const handleCancel = () => {
        setShowNewPresetModal(false)
        setShowDuplicatePresetModal(false)
        setShowChangePresetModal(false)
        setShowDeletePresetModal(false)
        setShowActivatePresetModal(false)

        setChanged(false)

        setPresetPhysicalParameters(selectedPreset?.parameters || presetPhysicalParameters)
    }

    const handleActive = () => {
        setSelectedPreset(prev => {
            updatePreset(
                assetId,
                {
                    id: prev.id,
                    active: true
                } as PresetType,
                () => {
                    setPresetList(prevList => {
                        return prevList.map(item => {
                            return item.id === prev.id ? { ...item, active: true } : { ...item, active: false }
                        })
                    })
                }
            )

            return {
                ...prev,
                active: true
            }
        })

        setShowActivatePresetModal(false)
        showAlert(i18n('Success!'), i18n('_preset_activate_message_', { name: selectedPreset.name }))
    }

    const handleRaname = (name: string) => {
        const newPreset = {
            id: selectedPreset.id,
            name
        }

        updatePreset(assetId, newPreset, response => {
            if (response.success) {
                setSelectedPreset({ ...response.preset })
                setPresetList(prev => prev.map(item => (item.id === response.preset.id ? response.preset : item)))

                showAlert(
                    i18n('Success!'),
                    i18n('_preset_rename_message_', { oldName: selectedPreset.name, newName: name })
                )
            }
        })

        setSelectedPreset(prev => ({ ...prev, name }))
        setShowChangePresetModal(false)
    }
    const handleTableChange = (_preset: PresetDataType) => {
        setChanged(true)
        setConvertedPhysicalModelParameters(_preset)
    }

    const getUnitConfigName = useCallback(() => {
        const localUnitSet = localStorage.getItem(LOCAL_UNIT_SET_NAME) || 'meter'
        const localUnitSetTimestamp = localStorage.getItem(LOCAL_UNIT_SET_TIMESTAMP)

        ConfigApi.getAnnotationConfig()
            .then(config => {
                setUnitSetName(config.unitSetName)
                if (!localUnitSetTimestamp && config.unitSetName !== 'meter') {
                    showAlert(
                        <label
                            style={{
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '16px',
                                textAlign: 'left'
                            }}
                        >
                            {i18n('Paramater units have been updated!')}
                        </label>,
                        i18n('_change_config_unit_', {
                            oldUnit: i18n(localUnitSet),
                            newUnit: i18n(config.unitSetName)
                        }),
                        'info',
                        true
                    )
                }

                if (localUnitSetTimestamp && config.unitSetName !== localUnitSet) {
                    showAlert(
                        <label
                            style={{
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '16px',
                                textAlign: 'left'
                            }}
                        >
                            {i18n('Paramater units have been updated!')}
                        </label>,
                        i18n('_change_config_unit_', {
                            oldUnit: i18n(localUnitSet),
                            newUnit: i18n(config.unitSetName)
                        }),
                        'info',
                        true
                    )
                }
                if (localUnitSet !== config.unitSetName) {
                    localStorage.setItem(LOCAL_UNIT_SET_TIMESTAMP, Date.now().toString())
                    localStorage.setItem(LOCAL_UNIT_SET_NAME, config.unitSetName)
                }
            })
            .catch(e => console.log({ e }))
    }, [])

    const showAlert = (
        title: React.ReactElement | string,
        message: string,
        type: 'success' | 'danger' | 'warning' | 'info' = 'success',
        never?: boolean
    ) => {
        setAlertMessage(message)
        setAlertTitle(title as string)
        setAlertType(type)
        setShowAlertMessage(true)
        setNeverClose(never || false)
    }

    const handleDismissAll = () => {
        dismissAll(assetId, selectedPreset, ({ preset }) => {
            if (!_.isEmpty(preset.parameters)) {
                const outPreset = _.keys(convertedPhysicalModelParameters).reduce(
                    (newpreset, parameter) => ({
                        ...newpreset,
                        [parameter]: {
                            value: convertedPhysicalModelParameters[parameter]?.value,
                            timestamp:
                                preset.parameters?.[parameter]?.timestamp ||
                                convertedPhysicalModelParameters[parameter]?.timestamp
                        }
                    }),
                    {} as PresetDataType
                )

                setConvertedPhysicalModelParameters(outPreset)
            }
        })
    }

    useEffect(() => {
        getListPresetRequest(assetId, setPresetList)
        getUnitConfigName()
    }, [assetId])

    useEffect(() => {
        getPresetDataByIdRequest(assetId, selectedPreset?.id, presetData => {
            if (!_.isEmpty(presetData)) setPresetPhysicalParameters(presetData)
        })
    }, [selectedPreset?.id])

    useEffect(() => {
        const defaultPreset = presetList?.find(p => p.active) || presetList[0]
        defaultPreset && setSelectedPreset(defaultPreset)
    }, [presetList])

    useEffect(() => {
        _.forEach(globalPreset, (parameter, name) => {
            const globalChanged = parameter?.timestamp > presetPhysicalParameters[name]?.timestamp

            setGlobalChanged(false)
            if (parameter?.value !== presetPhysicalParameters[name]?.name && globalChanged) {
                setGlobalChanged(true)

                return false
            }
        })
    }, [unitSetName, presetPhysicalParameters, globalPreset, selectedPreset?.id])

    useEffect(() => {
        getGlobalPreset(global => {
            setGlobalPreset(global || ({} as PresetDataType))
        })
    }, [])

    useEffect(() => {
        !_.isEmpty(globalPreset) &&
            convertPreset(globalPreset, newPreset => setConvertedGlobalPreset(newPreset), 'default', unitSetName)
    }, [unitSetName, globalPreset])

    useEffect(() => {
        !_.isEmpty(presetPhysicalParameters) &&
            convertPreset(
                presetPhysicalParameters,
                newParameter => {
                    setConvertedPhysicalModelParameters(newParameter)
                    setConvertedSelectedPreset(prev => {
                        const newPreset = _.cloneDeep(prev)
                        newPreset.parameters = _.cloneDeep(newParameter)

                        return newPreset
                    })
                },
                'default',
                unitSetName
            )
    }, [unitSetName, presetPhysicalParameters])

    const allowDeletePreset = selectedPreset?.name === STANDARD_PARAMETERS || selectedPreset?.active || changed

    return (
        <StyledFullScreenModal
            onHide={() => {
                handleUnSave(handleClose, handleClose)
            }}
            title={i18n('Physical Models Configuration')}
            subtitle={assetName}
            hideFooter={true}
            show={show}
            showAlert={showalertMessage}
        >
            <ModalContent>
                <ModalBody>
                    <SideContent>
                        <PresetList
                            selectedId={selectedPreset?.id}
                            presetList={presetList}
                            changeSelectedPreset={changeSelectedPreset}
                            setShowNewPresetModal={show => {
                                handleUnSave(() => setShowNewPresetModal(show))
                            }}
                        />
                    </SideContent>
                    <Content>
                        <ContentHeader>
                            <Title>
                                <h3>{selectedPreset?.name}</h3>
                                {selectedPreset?.name !== STANDARD_PARAMETERS && (
                                    <i
                                        style={{ opacity: changed && '0.7', cursor: changed && 'initial' }}
                                        className="fa fa-pen"
                                        onClick={() => !changed && setShowChangePresetModal(true)}
                                    />
                                )}
                            </Title>
                            <HeaderOptions>
                                <OutlineButton
                                    disabled={changed}
                                    onClick={() => handleUnSave(() => setShowDuplicatePresetModal(true))}
                                >
                                    <i className="far fa-copy" />
                                    <span>{i18n('Duplicate preset')}</span>
                                </OutlineButton>

                                <Tooltips
                                    position="top"
                                    content={i18n(
                                        'This preset is active! To delete it, you must activate another preset.'
                                    )}
                                    hide={!selectedPreset?.active}
                                >
                                    <OutlineButton
                                        style={{
                                            color: !allowDeletePreset ? 'var(--red-500)' : 'var(--red-300)',
                                            cursor: allowDeletePreset ? 'not-allowed' : undefined
                                        }}
                                        onClick={() => !allowDeletePreset && setShowDeletePresetModal(true)}
                                    >
                                        <i className="far fa-trash" />
                                        <span>{i18n('Delete preset')}</span>
                                    </OutlineButton>
                                </Tooltips>
                                <Tooltips
                                    position="top"
                                    content={i18n(
                                        'This preset is already active! To deactivate it, you must activate another preset.'
                                    )}
                                    hide={!selectedPreset?.active}
                                >
                                    <Button
                                        style={{
                                            background: selectedPreset?.active ? 'var(--green-500)' : 'var(--blue-500)',
                                            cursor: selectedPreset?.active ? 'not-allowed' : undefined
                                        }}
                                        onClick={() => !selectedPreset?.active && setShowActivatePresetModal(true)}
                                        // disabled={selectedPreset?.active}
                                    >
                                        <i className={selectedPreset?.active ? 'fa fa-check' : 'fa fa-play'} />
                                        <span>{selectedPreset?.active ? i18n('active') : i18n('activate')}</span>
                                    </Button>
                                </Tooltips>
                            </HeaderOptions>
                        </ContentHeader>

                        <PresetSheet
                            changed={changed}
                            unitSet={unitSetName}
                            onChange={handleTableChange}
                            setCanEdit={setEditTable}
                            editTable={editTable}
                            onSave={handleSubmit}
                            onCancel={handleCancel}
                            physicalModelParameters={convertedPhysicalModelParameters}
                            globalPhysicalModelParameters={convertedGlobalPreset}
                        />

                        {globalChanged && (
                            <div className="dismissAll">
                                <p>
                                    {' '}
                                    {i18n('_dismiss_all_')} <a onClick={handleDismissAll}>{i18n('Dismiss all')}</a>
                                </p>
                            </div>
                        )}
                    </Content>
                </ModalBody>
            </ModalContent>
            <NewPresetModal
                save={handleNewPreset}
                close={handleCancel}
                show={showNewPresetModal}
                duplicated={false}
                name=""
                presetList={presetList}
            />
            <NewPresetModal
                save={handleDuplicate}
                close={handleCancel}
                show={showDuplicatePresetModal}
                duplicated={true}
                name={selectedPreset?.name}
                presetList={presetList}
            />
            <NewPresetModal
                save={handleRaname}
                close={handleCancel}
                show={showChangePresetModal}
                duplicated={false}
                name={selectedPreset?.name}
                presetList={presetList}
            />
            <DeletePresetModal
                name={selectedPreset?.name}
                save={handleDelete}
                close={handleCancel}
                show={showDeletePresetModal}
            />
            <ActivatePresetModal
                name={selectedPreset?.name}
                activePresetName={presetList?.find(p => p.active)?.name}
                save={handleActive}
                close={handleCancel}
                show={showActivatePresetModal}
            />
            <GlobalAlert
                className={alertType}
                show={showalertMessage}
                allowClose={alertType === 'info'}
                neverClose={alertType === 'info'}
                message={
                    alertType === 'info' ? (
                        <UnitAlert alertMessage={alertMessage} alertTitle={alertTitle} />
                    ) : (
                        <DefaultAlert alertMessage={alertMessage} alertTitle={alertTitle} />
                    )
                }
                onClose={() => {
                    setShowAlertMessage(false)
                }}
                // visibleTime={2}
            />
        </StyledFullScreenModal>
    )
}

const DefaultAlert = ({ alertTitle, alertMessage }) => {
    return (
        <>
            <span>
                <b>{alertTitle}</b>
            </span>
            <br />
            <span>{alertMessage}</span>
        </>
    )
}

const UnitAlert = ({ alertTitle, alertMessage }) => {
    return (
        <StyledUnitAlert>
            <div className="alert-unit-icon">
                <i className="far fa-exclamation-triangle" />
            </div>
            <div>
                <b className="alert-unit-title">{alertTitle}</b>

                <span className="alert-unit-message">{alertMessage}</span>
            </div>
        </StyledUnitAlert>
    )
}

const StyledFullScreenModal = styled(FullScreenModal)<{ shoAlert: boolean }>`
    .live-global-alert {
        //width: 600px;
        padding: 16px;
        display: ${({ showAlert }) => (showAlert ? 'flex' : 'none')};
    }
    background-color: white;
`
const StyledUnitAlert = styled.div`
    display: flex;
    align-items: start;
    flex-direction: row;
    margin: 8px;
    .alert-unit-icon {
        margin-right: 8px;
    }
    .alert-unit-message {
        text-align: start;
    }
    .btn-close {
        align-self: self-start;
        justify-self: start;
    }
`
const parseNumberPreset = (
    { hookLoadWithoutBlockWeight, enableSSAFiltering, ...preset }: PresetDataType,
    {
        hookLoadWithoutBlockWeight: globalHookLoadWithoutBlockWeight,
        enableSSAFiltering: globalEnableSSAFiltering,
        ...globalPreset
    }: PresetDataType
): PresetDataType => {
    const parsedParameters = _.reduce(
        preset,
        (newPreset, { value, timestamp }, parameterName) => {
            newPreset[parameterName] = {
                value: parseFloat(!_.isNull(value) ? value.toString() : globalPreset[parameterName].value),
                timestamp
            }

            return newPreset
        },
        {} as PresetDataType
    )

    return { hookLoadWithoutBlockWeight, enableSSAFiltering, ...parsedParameters }
}

const convertPreset = (
    preset: PresetDataType,
    callBack: (preset: PresetDataType) => void,
    sourceUnit: UnitConfig,
    targetUnit: UnitConfig,
    source?: string
) => {
    if (sourceUnit === targetUnit) {
        callBack(preset)

        return
    }
    const dataParamters = getPhysicalModelParameters(sourceUnit)
    const convertedPreset = { ...preset }
    const labelNames = {}
    for (const param of ConfigAlgorithm.constantsRigStateList) {
        labelNames[param.key] = param.label
    }

    const presetConstants: ConstantConfigType[] = Object.keys(convertedPreset)
        .filter(paramName => dataParamters[paramName]?.unit !== '-')
        .map(param => ({
            key: param,
            label: labelNames[param],
            unit: dataParamters[param]?.unit,
            value: preset[param].value
        }))
    convertConstants(presetConstants, targetUnit).then(newValues => {
        for (const paramName of Object.keys(newValues)) {
            if (newValues[paramName]?.targetValue) {
                convertedPreset[paramName].value = parseFloat(newValues[paramName].targetValue.toFixed(8))
            }
        }

        return callBack(convertedPreset)
    })
}

const ModalBody = styled.div`
    width: 100%;
    background-color: var(--gray-0);
    overflow-x: hidden;

    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
`

const Content = styled.div`
    width: 100%;
    padding: 0 40px;
    padding-top: 15px;
    display: flex;

    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;
    .dismissAll {
        margin-top: 24px;
        p {
            font-weight: 400;
            font-size: 14px;

            span {
                color: var(--red-500);
            }
        }
    }
`

const ContentHeader = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`

const Title = styled.div`
    height: 32px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    margin: 15px 0;

    > h3 {
        font-family: ${({ theme }) => theme['font-bold']};
        font-size: 18px;
        font-weight: 800;
        text-transform: capitalize;

        margin-right: 16px;
        padding-left: 8px;
    }

    > i {
        color: var(--blue-500);
        cursor: pointer;
    }
`

const HeaderOptions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`

const SideContent = styled.div`
    background: var(--gray-200);
    width: 360px;
    height: 100%;
    display: flex;
    overflow-y: auto;
    flex-direction: column;
    @media (max-width: 800px) {
        width: 250px;
    }
    border-right: 1px solid ${({ theme }) => theme['gray-dark-bg']};
`
